import { logReportDownload } from '../../util/auditLogger';
import React, { useEffect, useState, useMemo } from "react";
import Context from "../../Context";
import { useSearchParams } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import { Spinner } from "../../components/ui";
import { Header } from "../../components/modules";
import {
  FiFileText,
  FiSearch,
  FiEye,
  FiArrowLeft,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { FaTimes, FaExternalLinkAlt } from "react-icons/fa";
import { doc, onSnapshot } from "firebase/firestore";
import {
  RiStackLine,
  RiCheckDoubleLine,
  RiTimeLine,
  RiAlertLine,
  RiArchiveLine,
  RiFileExcel2Line,
} from "react-icons/ri";
import * as xlsx from "xlsx";

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (ts) => {
  if (!ts) return "-";
  return (
    new Date(Number(ts)).toLocaleDateString("th-TH", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    }) + " น."
  );
};



// ── StatCard (Workspace Rules: text-xl font-bold, text-sm font-semibold, rounded-md)

const StatCard = ({ label, value, subtext, icon: Icon, badge, colorClass }) => (
  <div className="flex flex-col justify-between p-4 bg-white rounded-md shadow-xs border border-slate-200 hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold text-slate-500">{label}</span>
      {Icon && <Icon className={`text-base ${colorClass || "text-slate-400"}`} />}
    </div>
    <div className="mt-2 flex items-baseline justify-between">
      <div className="text-xl font-bold text-slate-900 tracking-tight">
        {value === "loading" ? (
          <Spinner className="w-4 h-4" />
        ) : (
          (Number(value) || 0).toLocaleString()
        )}
        <span className="text-xs font-semibold text-slate-400 ml-1">ใบ</span>
      </div>
      {badge && (
        <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-600">
          {badge}
        </span>
      )}
    </div>
    {subtext && (
      <p className="text-[11px] font-medium text-slate-400 mt-1">{subtext}</p>
    )}
  </div>
);

// ── StatusBadge for ticket items ──────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  switch (status) {
    case "new":
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
          ใหม่
        </span>
      );
    case "dup":
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold bg-sky-50 text-sky-700 border border-sky-200">
          ซ้ำ
        </span>
      );
    case "bad":
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200">
          ไม่ผ่าน
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200">
          {status || "Error"}
        </span>
      );
  }
};

// ── PaginationControl (Workspace Rules: rounded-md, Navy active state) ────────

const PaginationControl = ({
  currentPage,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  itemLabel = "รายการ",
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const startIndex = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, totalItems);

  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = [];
    if (currentPage <= 4) {
      pages.push(1, 2, 3, 4, 5, "...", totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(
        1,
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages
      );
    } else {
      pages.push(
        1,
        "...",
        currentPage - 1,
        currentPage,
        currentPage + 1,
        "...",
        totalPages
      );
    }
    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="p-3 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 bg-white">
      {/* Left: Summary & Page Size selector */}
      <div className="flex items-center gap-3 text-xs text-slate-600 font-semibold">
        <span>
          แสดง <span className="font-bold text-slate-900">{startIndex.toLocaleString()}</span> -{" "}
          <span className="font-bold text-slate-900">{endIndex.toLocaleString()}</span> จาก{" "}
          <span className="font-bold text-slate-900">{totalItems.toLocaleString()}</span> {itemLabel}
        </span>
        {onPageSizeChange && (
          <div className="flex items-center gap-1.5 ml-2 border-l border-slate-200 pl-3">
            <span className="text-slate-400 font-medium">แถวต่อหน้า:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                onPageSizeChange(Number(e.target.value));
                onPageChange(1);
              }}
              className="h-8 px-2 rounded-md border border-slate-300 bg-white text-xs font-semibold text-slate-700 focus:outline-hidden focus:border-navy focus:ring-1 focus:ring-navy cursor-pointer"
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Right: Page Navigation */}
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="flex items-center gap-1 h-8 px-2.5 rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          title="หน้าก่อนหน้า"
        >
          <FiChevronLeft className="text-sm" />
          <span className="hidden sm:inline">ก่อนหน้า</span>
        </button>

        {pageNumbers.map((p, idx) => {
          if (p === "...") {
            return (
              <span
                key={`ellipsis-${idx}`}
                className="w-8 h-8 flex items-center justify-center text-xs text-slate-400 font-bold"
              >
                ...
              </span>
            );
          }
          const isActive = currentPage === p;
          return (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={`min-w-[32px] h-8 px-2 rounded-md text-xs font-bold transition-colors ${isActive
                  ? "bg-navy text-white shadow-xs"
                  : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"
                }`}
            >
              {p}
            </button>
          );
        })}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="flex items-center gap-1 h-8 px-2.5 rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          title="หน้าถัดไป"
        >
          <span className="hidden sm:inline">ถัดไป</span>
          <FiChevronRight className="text-sm" />
        </button>
      </div>
    </div>
  );
};


// ── ImageModal (Lightbox for inspecting ticket in detail view) ─────────────────

const ImageModal = ({ item, bookInfo, onClose }) => {
  if (!item) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-in fade-in-0 duration-150"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-md shadow-2xl max-w-lg w-full p-5 border border-slate-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between pb-3 border-b border-slate-100">
          <div>
            <h3 className="text-base font-bold text-slate-900">
              ตรวจสลากกินแบ่งรัฐบาล
            </h3>
            <p className="text-xs text-slate-500 font-semibold">
              เล่ม: {bookInfo?.fileNameId || "-"} | รหัสใบ: {item.id}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <FaTimes className="text-base" />
          </button>
        </div>

        <div className="my-4 flex justify-center bg-slate-50 p-2 rounded-md border border-slate-100">
          {item.image ? (
            <img
              src={item.image}
              alt={item.number}
              className="max-h-[340px] w-auto object-contain rounded-md shadow-xs"
            />
          ) : (
            <div className="h-48 flex items-center justify-center text-slate-400 text-xs">
              ไม่มีรูปภาพสลาก
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 p-3 rounded-md border border-slate-200/60">
          <div>
            <span className="text-slate-400 font-medium">หมายเลขสลาก:</span>
            <p className="font-mono font-bold text-slate-900 text-lg tracking-widest">
              {item.number || "-"}
            </p>
          </div>
          <div>
            <span className="text-slate-400 font-medium">สถานะ:</span>
            <div className="mt-0.5">
              <StatusBadge status={item.status} />
            </div>
          </div>
          <div>
            <span className="text-slate-400 font-medium">เล่ม (Book No):</span>
            <p className="font-semibold text-slate-800">{item.bookNo ?? "-"}</p>
          </div>
          <div>
            <span className="text-slate-400 font-medium">เวลาประมวลผล:</span>
            <p className="font-semibold text-slate-800">
              {item.processedAt ? formatDate(item.processedAt) : "-"}
            </p>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="py-2 px-4 text-xs font-bold rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
          >
            ปิดหน้าต่าง
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const ImportHistory = () => {
  const { callApi, configs, db } = Context();
  const [searchParams, setSearchParams] = useSearchParams();
  const importId = searchParams.get("id");

  // Dashboard live stats (onBlock / offBlock)
  const [dashboardStats, setDashboardStats] = useState({
    onBlock: "loading",
    offBlock: "loading",
  });

  // List-view state
  const [imports, setImports] = useState([]);
  const [searchBook, setSearchBook] = useState("");
  const [listPage, setListPage] = useState(1);
  const [listPageSize, setListPageSize] = useState(20);

  // Detail-view state
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [searchNumber, setSearchNumber] = useState("");
  const [previewItem, setPreviewItem] = useState(null);
  const [detailPage, setDetailPage] = useState(1);
  const [detailPageSize, setDetailPageSize] = useState(20);

  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportStatus, setExportStatus] = useState("");

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchImports = async () => {
    if (!configs?.roundDate) return;
    setLoading(true);
    const res = await callApi(
      "lotteries-getImports",
      { roundDate: configs.roundDate, limit: 300 },
      { notShowLoading: true }
    );
    setImports(res?.data || []);
    setListPage(1);
    setLoading(false);
  };

  const fetchItems = async () => {
    if (!importId) return;
    setLoading(true);
    const res = await callApi(
      "lotteries-getImportItems",
      { importId },
      { notShowLoading: true }
    );
    setItems(res?.data || []);
    setDetailPage(1);
    setLoading(false);
  };

  // Always keep imports loaded
  useEffect(() => {
    fetchImports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configs?.roundDate]);

  // Real-time Firestore listener for onBlock / offBlock counts
  useEffect(() => {
    if (!db || !configs?.roundDate) return;
    try {
      const unsub = onSnapshot(
        doc(db, "Dashboard", String(configs.roundDate)),
        (snap) => {
          if (snap.exists()) {
            const d = snap.data();
            setDashboardStats({
              onBlock: Number(d.currentLottoOnBlock) || 0,
              offBlock: Number(d.currentLottoOffBlock) || 0,
            });
          } else {
            setDashboardStats({ onBlock: 0, offBlock: 0 });
          }
        },
        (err) => {
          console.warn("importHistory onSnapshot warning:", err);
          setDashboardStats({ onBlock: 0, offBlock: 0 });
        }
      );
      return () => unsub();
    } catch (err) {
      console.warn("importHistory onSnapshot setup failed:", err);
      setDashboardStats({ onBlock: 0, offBlock: 0 });
    }
  }, [db, configs?.roundDate]);

  // Load items when importId is present
  useEffect(() => {
    if (importId) {
      setFilter("all");
      setSearchNumber("");
      setDetailPage(1);
      fetchItems();
    } else {
      setItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importId]);

  // Find currently opened book info
  const currentImport = useMemo(() => {
    if (!importId) return null;
    return imports.find((x) => String(x.createdAt) === String(importId));
  }, [imports, importId]);

  // ── Detail-view calculations ───────────────────────────────────────────────

  const detailCounts = useMemo(() => {
    return items.reduce(
      (acc, item) => {
        acc[item.status] = (acc[item.status] || 0) + 1;
        return acc;
      },
      { new: 0, dup: 0, bad: 0 }
    );
  }, [items]);

  const filteredItems = useMemo(() => {
    let result = items;
    if (filter !== "all") {
      result = result.filter((i) => i.status === filter);
    }
    if (searchNumber.trim()) {
      const q = searchNumber.trim().toLowerCase();
      result = result.filter(
        (i) =>
          (i.number || "").toLowerCase().includes(q) ||
          (i.id || "").toLowerCase().includes(q) ||
          String(i.bookNo || "").includes(q)
      );
    }
    return result;
  }, [items, filter, searchNumber]);

  const paginatedItems = useMemo(() => {
    const start = (detailPage - 1) * detailPageSize;
    return filteredItems.slice(start, start + detailPageSize);
  }, [filteredItems, detailPage, detailPageSize]);

  // ── List-view calculations ─────────────────────────────────────────────────

  const listTotals = useMemo(() => {
    return imports.reduce(
      (acc, imp) => {
        acc.found += imp.found || 0;
        acc.newLotteries += imp.newLotteries || 0;
        acc.dubLotteries += imp.dubLotteries || 0;
        acc.bad += imp.bad || 0;
        return acc;
      },
      { found: 0, newLotteries: 0, dubLotteries: 0, bad: 0 }
    );
  }, [imports]);

  const filteredImports = useMemo(() => {
    if (!searchBook.trim()) return imports;
    const q = searchBook.trim().toLowerCase();
    return imports.filter(
      (imp) =>
        (imp.fileNameId || "").toLowerCase().includes(q) ||
        (imp.uploader || "").toLowerCase().includes(q)
    );
  }, [imports, searchBook]);

  const paginatedImports = useMemo(() => {
    const start = (listPage - 1) * listPageSize;
    return filteredImports.slice(start, start + listPageSize);
  }, [filteredImports, listPage, listPageSize]);

  // Re-sync & Export active books in the system to Excel (identical to reports/book-summary)
  const handleExportExcel = async () => {
    if (!configs?.roundDate || exportLoading) return;
    setExportLoading(true);
    setExportStatus("กำลังคำนวณยอดใหม่ (Re-sync)...");
    try {
      // 1. Re-sync counts ground-truth from Lotteries
      await callApi(
        "books-countAllLotteriesToBooks",
        { roundDate: configs.roundDate },
        { notShowLoading: true }
      );

      setExportStatus("กำลังดึงข้อมูลเล่มสลาก...");
      // 2. Fetch all books
      const res = await callApi(
        "books-getAllBooks",
        {
          queries: [["roundDate", "==", configs.roundDate]],
          limit: 50000,
        },
        { notShowLoading: true }
      );

      const books = Array.isArray(res) ? res : (res?.data || []);
      if (!books || books.length === 0) {
        alert("ไม่พบข้อมูลเล่มสลากที่นำเข้าระบบในงวดนี้");
        setExportLoading(false);
        setExportStatus("");
        return;
      }

      setExportStatus("กำลังสร้างไฟล์ Excel...");

      // สรุปยอดตามผู้รับผิดชอบ
      const uploaderSummary = {};
      let totalLotto = 0;
      books.forEach((book) => {
        const key = book.uploader || "ไม่ระบุ";
        const count = book.count || book.lotteryIds?.length || 0;
        totalLotto += count;
        if (!uploaderSummary[key]) {
          uploaderSummary[key] = { bookCount: 0, lottoCount: 0 };
        }
        uploaderSummary[key].bookCount++;
        uploaderSummary[key].lottoCount += count;
      });

      // เรียงลำดับชื่อเล่มสลาก
      books.sort((a, b) => (a.id || "").localeCompare(b.id || ""));

      // โครงสร้างเดียวกับหน้า reports/book-summary (ReportLotterieBooksImport)
      const listHead = [];
      listHead.push(["", `รายงานสรุปการนำเล่มเข้าระบบ งวด ${configs.roundDate}`]);
      listHead.push(["ชื่อผู้รับผิดชอบ", "จำนวนเล่ม", "จำนวนล็อตเตอรี่"]);

      Object.entries(uploaderSummary).forEach(
        ([uploader, { bookCount, lottoCount }]) => {
          listHead.push([uploader, bookCount, lottoCount]);
        }
      );

      listHead.push([]);
      listHead.push([
        "ลำดับที่",
        `ชื่อเล่ม (${books.length} เล่ม)`,
        `จำนวน (${totalLotto} ใบ)`,
        "สร้างเมื่อ",
        "ผู้รับผิดชอบ",
      ]);

      books.forEach((book, index) => {
        let formattedTime = "-";
        if (book.createdAt) {
          try {
            const timeNum = Number(book.createdAt);
            if (!isNaN(timeNum) && timeNum > 0) {
              formattedTime = formatDate(timeNum);
            } else if (book.createdAt && typeof book.createdAt.toDate === "function") {
              formattedTime = formatDate(book.createdAt.toDate().getTime());
            } else {
              formattedTime = new Date(book.createdAt).toLocaleDateString("th-TH", {
                year: "numeric",
                month: "numeric",
                day: "numeric",
                hour: "numeric",
                minute: "numeric",
              }) + " น.";
            }
          } catch (e) {
            formattedTime = String(book.createdAt);
          }
        }

        listHead.push([
          index + 1,
          book.id || book.fileNameId || "-",
          book.count || book.lotteryIds?.length || 0,
          formattedTime,
          book.uploader || "-",
        ]);
      });

      const ws = xlsx.utils.aoa_to_sheet(listHead);
      ws["!cols"] = [
        { wch: 10 }, // ลำดับที่
        { wch: 32 }, // ชื่อเล่ม
        { wch: 20 }, // จำนวน (ใบ)
        { wch: 26 }, // สร้างเมื่อ
        { wch: 24 }, // ผู้รับผิดชอบ
      ];

      const wb = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(wb, ws, "Books");

      logReportDownload();
      xlsx.writeFile(wb, `รายงานการนำเข้าเล่มสลาก_${configs.roundDate}.xlsx`);
    } catch (err) {
      console.error("Export books error:", err);
      alert("เกิดข้อผิดพลาดในการคำนวณและดาวน์โหลดข้อมูลเล่มสลาก");
    } finally {
      setExportLoading(false);
      setExportStatus("");
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  // VIEW 1: DETAIL VIEW (เจาะลึก 100 ใบในเล่มที่เลือก)
  // ───────────────────────────────────────────────────────────────────────────

  if (importId) {
    const DETAIL_STATS = [
      {
        label: "สลากทั้งหมดในเล่ม",
        value: loading ? "loading" : items.length,
        subtext: `เล่ม ${currentImport?.fileNameId || importId}`,
        icon: RiStackLine,
        colorClass: "text-slate-600",
      },
      {
        label: "สลากใหม่",
        value: loading ? "loading" : detailCounts.new,
        subtext: "นำเข้าสู่ระบบสำเร็จ",
        icon: RiCheckDoubleLine,
        colorClass: "text-emerald-600",
        badge:
          items.length > 0
            ? `${((detailCounts.new / items.length) * 100).toFixed(0)}%`
            : undefined,
      },
      {
        label: "สลากซ้ำ",
        value: loading ? "loading" : detailCounts.dup,
        subtext: "ซ้ำกับเล่มอื่นที่นำเข้าก่อนหน้า",
        icon: RiTimeLine,
        colorClass: "text-sky-600",
      },
      {
        label: "สลากไม่ผ่าน (Bad)",
        value: loading ? "loading" : detailCounts.bad,
        subtext: "สแกนไม่สมบูรณ์ / OCR ไม่ผ่าน",
        icon: RiAlertLine,
        colorClass: "text-rose-600",
      },
    ];

    return (
      <div className="h-fit w-full min-w-[1024px] max-w-[1600px] mx-auto pb-12 space-y-4">
        {/* Header with Back Button */}
        <div className="flex items-center justify-between">
          <Header
            header="Import Detail"
            subHeader={
              <div className="flex items-center gap-2 mt-0.5">
                <span className="font-semibold text-slate-700">
                  เล่ม: {currentImport?.fileNameId || importId}
                </span>
                <span className="text-slate-300">|</span>
                <span className="text-xs text-slate-500">
                  ผู้นำเข้า: {currentImport?.uploader || "-"}
                </span>
                <span className="text-slate-300">|</span>
                <span className="text-xs text-slate-500">
                  {formatDate(currentImport?.createdAt || importId)}
                </span>
              </div>
            }
            icon={<FiFileText className="text-navy" />}
          />

          <div className="flex items-center gap-2">
            {detailCounts.bad > 0 && (
              <a
                href="/lotteries/unimported"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-xs font-bold hover:bg-rose-100 transition-colors shadow-xs"
              >
                <span>จัดการสลากไม่ผ่าน ({detailCounts.bad})</span>
                <FaExternalLinkAlt className="text-[10px]" />
              </a>
            )}

            <button
              onClick={() => setSearchParams({})}
              className="flex items-center gap-1.5 px-4 py-2 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold shadow-xs transition-colors"
            >
              <FiArrowLeft className="text-sm" />
              <span>กลับไปหน้ารวมเล่ม</span>
            </button>
          </div>
        </div>

        {/* 4 Detail Stat Cards */}
        <div className="grid grid-cols-4 gap-3">
          {DETAIL_STATS.map((stat) => (
            <StatCard key={stat.label} {...stat} />
          ))}
        </div>

        {/* Table Card */}
        <div className="relative bg-white w-full rounded-md shadow-xs border border-slate-200 overflow-hidden">
          {/* Status Filter Tabs */}
          <div className="flex items-center gap-1 p-3 border-b border-slate-200 bg-slate-50/50 overflow-x-auto">
            {[
              { id: "all", label: "ทั้งหมด", count: items.length },
              { id: "new", label: "สลากใหม่", count: detailCounts.new },
              { id: "dup", label: "สลากซ้ำ", count: detailCounts.dup },
              { id: "bad", label: "ไม่ผ่าน (Bad)", count: detailCounts.bad },
            ].map((tab) => {
              const isActive = filter === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setFilter(tab.id);
                    setDetailPage(1);
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all shrink-0 ${isActive
                    ? "bg-navy text-white shadow-xs"
                    : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
                    }`}
                >
                  <span>{tab.label}</span>
                  <span
                    className={`text-[10px] px-1.5 py-0.2 rounded-md ${isActive
                      ? "bg-white/20 text-white"
                      : "bg-slate-100 text-slate-500 font-mono"
                      }`}
                  >
                    {tab.count.toLocaleString()}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Search Toolbar */}
          <div className="p-3 border-b border-slate-200 flex items-center justify-between gap-3 bg-white">
            <div className="relative flex items-center">
              <input
                type="text"
                placeholder="ค้นหาหมายเลขสลาก หรือรหัสใบ..."
                value={searchNumber}
                onChange={(e) => {
                  setSearchNumber(e.target.value);
                  setDetailPage(1);
                }}
                className="w-64 h-9 pl-3 pr-8 rounded-md border border-slate-300 text-xs font-semibold text-slate-800 placeholder:text-slate-400 focus:outline-hidden focus:border-navy focus:ring-1 focus:ring-navy"
              />
              {searchNumber && (
                <button
                  onClick={() => {
                    setSearchNumber("");
                    setDetailPage(1);
                  }}
                  className="absolute right-2.5 text-slate-400 hover:text-slate-600"
                >
                  <FaTimes className="text-xs" />
                </button>
              )}
            </div>

            <div className="text-xs font-semibold text-slate-500">
              ทั้งหมด {filteredItems.length.toLocaleString()} ใบ (จาก {items.length.toLocaleString()} ใบ)
            </div>
          </div>

          {/* Detail Table */}
          <div className="relative min-h-[420px]">
            {loading && (
              <div className="absolute inset-0 bg-white/70 backdrop-blur-xs flex items-center justify-center z-20">
                <div className="flex flex-col items-center gap-2">
                  <Spinner className="w-6 h-6" />
                  <span className="text-xs font-bold text-slate-600">
                    กำลังโหลดสลากในเล่ม...
                  </span>
                </div>
              </div>
            )}

            <Table>
              <TableHeader className="bg-slate-50 border-b border-slate-200">
                <TableRow>
                  <TableHead className="text-center w-14 font-bold text-slate-700 text-xs">
                    ลำดับ
                  </TableHead>
                  <TableHead className="text-center w-28 font-bold text-slate-700 text-xs">
                    รูปสลาก
                  </TableHead>
                  <TableHead className="text-center w-40 font-bold text-slate-700 text-xs">
                    เลขสลาก
                  </TableHead>
                  <TableHead className="text-center w-24 font-bold text-slate-700 text-xs">
                    เลขเล่ม
                  </TableHead>
                  <TableHead className="text-center font-bold text-slate-700 text-xs">
                    รหัสสลาก (ID)
                  </TableHead>
                  <TableHead className="text-center w-28 font-bold text-slate-700 text-xs">
                    สถานะ
                  </TableHead>
                  <TableHead className="text-center font-bold text-slate-700 text-xs">
                    เวลาประมวลผล
                  </TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {paginatedItems.length > 0 ? (
                  paginatedItems.map((item, index) => (
                    <TableRow key={item.id} className="h-16 hover:bg-slate-50/60 transition-colors">
                      <TableCell className="font-medium text-center text-xs text-slate-500">
                        {(detailPage - 1) * detailPageSize + index + 1}
                      </TableCell>
                      <TableCell className="text-center">
                        <div
                          className="relative group mx-auto w-20 h-10 rounded-md overflow-hidden border border-slate-200 cursor-pointer shadow-xs"
                          onClick={() => setPreviewItem(item)}
                          title="คลิกเพื่อขยายรูปภาพสลาก"
                        >
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={item.number}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            />
                          ) : (
                            <div className="w-full h-full bg-slate-100 flex items-center justify-center text-[10px] text-slate-400">
                              ไม่มีรูป
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white">
                            <FiEye className="text-sm" />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-mono font-bold text-slate-900 text-base tracking-widest">
                          {item.number || "-"}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-center text-xs font-semibold text-slate-700">
                        {item.bookNo ?? "-"}
                      </TableCell>
                      <TableCell className="font-mono text-center text-xs text-slate-500">
                        {item.id}
                      </TableCell>
                      <TableCell className="text-center">
                        <StatusBadge status={item.status} />
                      </TableCell>
                      <TableCell className="text-center text-xs text-slate-500">
                        {item.processedAt ? formatDate(item.processedAt) : "-"}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <div className="w-full flex flex-col justify-center items-center h-72 gap-2 text-slate-400 font-semibold text-sm">
                        {!loading && (
                          <>
                            <FiSearch className="text-3xl text-slate-300" />
                            <span>ไม่พบรายการสลากที่ตรงตามเงื่อนไข</span>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {filteredItems.length > 0 && (
            <PaginationControl
              currentPage={detailPage}
              totalItems={filteredItems.length}
              pageSize={detailPageSize}
              onPageChange={setDetailPage}
              onPageSizeChange={setDetailPageSize}
              pageSizeOptions={[20, 50, 100]}
              itemLabel="ใบ"
            />
          )}
        </div>

        {/* Lightbox Preview Modal */}
        {previewItem && (
          <ImageModal
            item={previewItem}
            bookInfo={currentImport}
            onClose={() => setPreviewItem(null)}
          />
        )}
      </div>
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // VIEW 2: LIST VIEW (ประวัติการนำเข้ารายเล่ม)
  // ───────────────────────────────────────────────────────────────────────────

  const LIST_STATS = [
    {
      label: "สลากที่นำเข้าทั้งหมด",
      value: loading ? "loading" : listTotals.found,
      subtext: `รวม ${imports.length.toLocaleString()} เล่ม`,
      icon: RiStackLine,
      colorClass: "text-slate-600",
    },
    // {
    //   label: "สลากใหม่ทั้งหมด",
    //   value: loading ? "loading" : listTotals.newLotteries,
    //   subtext: "นำเข้าสู่ระบบสำเร็จ",
    //   icon: RiCheckDoubleLine,
    //   colorClass: "text-emerald-600",
    //   badge:
    //     listTotals.found > 0
    //       ? `${((listTotals.newLotteries / listTotals.found) * 100).toFixed(0)}%`
    //       : undefined,
    // },
    {
      label: "สลากหลังบ้าน (สต๊อก)",
      value: dashboardStats.offBlock,
      subtext: "เก็บสำรองในคลัง",
      icon: RiArchiveLine,
      colorClass: "text-amber-600",
    },
    // {
    //   label: "สลากหน้าบ้าน (พร้อมขาย)",
    //   value: dashboardStats.onBlock,
    //   subtext: "แสดงบนหน้าร้านลูกค้า",
    //   icon: RiStore2Line,
    //   colorClass: "text-sky-600",
    // },
  ];

  return (
    <div className="h-fit w-full min-w-[1024px] max-w-[1600px] mx-auto pb-12 space-y-4">
      {/* Header */}
      <Header
        header="Import History"
        subHeader={`ประวัติการนำเข้าสลากตามเล่ม — งวด ${configs?.roundDate || "-"}`}
        icon={<FiFileText className="text-navy" />}
      />

      {/* 4 Summary Stat Cards */}
      <div className="grid grid-cols-4 gap-3">
        {LIST_STATS.map((stat) => (
          <StatCard key={stat.label} {...stat} />
        ))}
      </div>

      {/* Table Card */}
      <div className="relative bg-white w-full rounded-md shadow-xs border border-slate-200 overflow-hidden">
        {/* Search & Toolbar */}
        <div className="p-3 border-b border-slate-200 flex items-center justify-between gap-3 bg-white">
          <div className="flex items-center gap-2">
            <div className="relative flex items-center">
              <input
                type="text"
                placeholder="ค้นหาชื่อเล่ม หรือผู้อัปโหลด..."
                value={searchBook}
                onChange={(e) => {
                  setSearchBook(e.target.value);
                  setListPage(1);
                }}
                className="w-72 h-9 pl-3 pr-8 rounded-md border border-slate-300 text-xs font-semibold text-slate-800 placeholder:text-slate-400 focus:outline-hidden focus:border-navy focus:ring-1 focus:ring-navy"
              />
              {searchBook && (
                <button
                  onClick={() => {
                    setSearchBook("");
                    setListPage(1);
                  }}
                  className="absolute right-2.5 text-slate-400 hover:text-slate-600"
                >
                  <FaTimes className="text-xs" />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={handleExportExcel}
              disabled={exportLoading}
              className="flex items-center gap-1.5 h-9 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-xs font-bold shadow-xs disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
              title="คำนวณยอดสลากจริงใหม่ทั้งหมดแล้วดาวน์โหลดรายงาน Excel (.xlsx)"
            >
              {exportLoading ? (
                <Spinner className="w-3.5 h-3.5 text-white" />
              ) : (
                <RiFileExcel2Line className="text-base" />
              )}
              <span>{exportLoading ? (exportStatus || "กำลังประมวลผล...") : "คำนวณและดาวน์โหลด Excel"}</span>
            </button>
          </div>

          <div className="text-xs font-semibold text-slate-500">
            ทั้งหมด {filteredImports.length.toLocaleString()} เล่ม (จาก {imports.length.toLocaleString()} เล่ม)
          </div>
        </div>

        {/* Books Table */}
        <div className="relative min-h-[420px]">
          {loading && (
            <div className="absolute inset-0 bg-white/70 backdrop-blur-xs flex items-center justify-center z-20">
              <div className="flex flex-col items-center gap-2">
                <Spinner className="w-6 h-6" />
                <span className="text-xs font-bold text-slate-600">
                  กำลังโหลดประวัติการนำเข้า...
                </span>
              </div>
            </div>
          )}

          <Table>
            <TableHeader className="bg-slate-50 border-b border-slate-200">
              <TableRow>
                <TableHead className="text-center w-14 font-bold text-slate-700 text-xs">
                  ลำดับ
                </TableHead>
                <TableHead className="text-center w-40 font-bold text-slate-700 text-xs">
                  เวลานำเข้า
                </TableHead>
                <TableHead className="text-center font-bold text-slate-700 text-xs">
                  ชื่อเล่มสลาก
                </TableHead>
                <TableHead className="text-center w-24 font-bold text-slate-700 text-xs">
                  พบทั้งหมด
                </TableHead>
                <TableHead className="text-center w-20 font-bold text-slate-700 text-xs">
                  ใหม่
                </TableHead>
                <TableHead className="text-center w-20 font-bold text-slate-700 text-xs">
                  ซ้ำ
                </TableHead>
                <TableHead className="text-center w-20 font-bold text-slate-700 text-xs">
                  ไม่ผ่าน
                </TableHead>
                <TableHead className="text-center font-bold text-slate-700 text-xs">
                  ผู้อัปโหลด
                </TableHead>
                <TableHead className="text-center w-28 font-bold text-slate-700 text-xs">
                  การจัดการ
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {paginatedImports.length > 0 ? (
                paginatedImports.map((imp, index) => (
                  <TableRow
                    key={imp.createdAt}
                    className="h-14 hover:bg-slate-50/60 transition-colors"
                  >
                    <TableCell className="font-medium text-center text-xs text-slate-500">
                      {(listPage - 1) * listPageSize + index + 1}
                    </TableCell>
                    <TableCell className="text-center text-xs text-slate-500">
                      {imp.createdAt ? formatDate(imp.createdAt) : "-"}
                    </TableCell>
                    <TableCell className="font-mono font-bold text-center text-xs text-slate-800">
                      {imp.fileNameId}
                    </TableCell>
                    <TableCell className="font-bold text-center text-slate-700 text-xs">
                      {imp.found ?? 0}
                    </TableCell>
                    <TableCell className="font-bold text-center text-emerald-600 text-xs">
                      {imp.newLotteries ?? 0}
                    </TableCell>
                    <TableCell className="font-bold text-center text-sky-600 text-xs">
                      {imp.dubLotteries ?? 0}
                    </TableCell>
                    <TableCell className="font-bold text-center text-rose-600 text-xs">
                      {imp.bad ?? 0}
                    </TableCell>
                    <TableCell className="text-center text-sm text-slate-600 font-bold">
                      {imp.uploader || "-"}
                    </TableCell>
                    <TableCell className="text-center">
                      <button
                        onClick={() => setSearchParams({ id: imp.createdAt })}
                        className="py-1.5 px-3 rounded-md bg-navy hover:bg-[#002b75] text-white font-bold text-xs shadow-xs transition-colors"
                      >
                        ดูสลาก
                      </button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10}>
                    <div className="w-full flex flex-col justify-center items-center h-72 gap-2 text-slate-400 font-semibold text-sm">
                      {!loading && (
                        <>
                          <FiSearch className="text-3xl text-slate-300" />
                          <span>ไม่พบประวัติการนำเข้าที่ตรงตามเงื่อนไข</span>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {filteredImports.length > 0 && (
          <PaginationControl
            currentPage={listPage}
            totalItems={filteredImports.length}
            pageSize={listPageSize}
            onPageChange={setListPage}
            onPageSizeChange={setListPageSize}
            pageSizeOptions={[10, 20, 50, 100]}
            itemLabel="เล่ม"
          />
        )}
      </div>
    </div>
  );
};

export { ImportHistory, ImportHistory as ImportReport };
export default ImportHistory;
